/**
 * SectionRenderer - Renders section elements (white section box)
 * Implements white bordered sections with optional title
 */

import Konva from 'konva';
import { BaseRenderer } from '../core/BaseRenderer';
import { EditableElement } from '../editing/types';

export class SectionRenderer extends BaseRenderer {
  private backgroundRect: Konva.Rect | null = null;
  private titleText: Konva.Text | null = null;

  constructor(element: EditableElement) {
    super(element);
  }

  /**
   * Render the section element
   */
  render(): Konva.Node {
    const { width, height, data } = this.element;
    
    // White background with border
    this.backgroundRect = new Konva.Rect({
      width: width,
      height: height,
      fill: '#FFFFFF',
      stroke: '#E0E0E0',
      strokeWidth: 1,
      cornerRadius: 8,
    });
    
    this._node.add(this.backgroundRect);
    
    // Title text (if provided)
    if (data?.title) {
      const padding = data?.padding || 40;
      this.titleText = new Konva.Text({
        x: padding,
        y: padding,
        text: data.title,
        fontSize: data?.titleFontSize || 32,
        fontFamily: 'Inter, sans-serif',
        fontStyle: '600',
        fill: '#000000',
      });
      this._node.add(this.titleText);
    }
    
    return this._node;
  }

  /**
   * Update the section with new properties
   */
  update(updates: Partial<EditableElement>): void {
    this.updateElementData(updates);
    
    const { width, height, data } = this.element;
    
    // Update background
    if (this.backgroundRect) {
      if (updates.width !== undefined || updates.height !== undefined) {
        this.backgroundRect.width(width);
        this.backgroundRect.height(height);
      }
    }
    
    // Update title
    if (updates.data) {
      if (updates.data.title !== undefined) {
        if (updates.data.title && !this.titleText) {
          // Create title if not exists
          const padding = data?.padding || 40;
          this.titleText = new Konva.Text({
            x: padding,
            y: padding,
            text: updates.data.title,
            fontSize: data?.titleFontSize || 32,
            fontFamily: 'Inter, sans-serif',
            fontStyle: '600',
            fill: '#000000',
          });
          this._node.add(this.titleText);
        } else if (this.titleText) {
          if (updates.data.title) {
            this.titleText.text(updates.data.title);
          } else {
            this.titleText.destroy();
            this.titleText = null;
          }
        }
      }
      
      if (updates.data.padding !== undefined && this.titleText) {
        this.titleText.x(updates.data.padding);
        this.titleText.y(updates.data.padding);
      }
      
      if (updates.data.titleFontSize !== undefined && this.titleText) {
        this.titleText.fontSize(updates.data.titleFontSize);
      }
    }
  }

  /**
   * Set dimensions and update internal nodes
   */
  setDimensions(width: number, height: number): void {
    super.setDimensions(width, height);
    
    if (this.backgroundRect) {
      this.backgroundRect.width(width);
      this.backgroundRect.height(height);
    }
  }

  /**
   * Get title
   */
  getTitle(): string {
    return this.element.data?.title || '';
  }

  /**
   * Set title
   */
  setTitle(title: string): void {
    if (this.titleText) {
      this.titleText.text(title);
    }
    if (this.element.data) {
      this.element.data.title = title;
    } else {
      this.element.data = { title };
    }
  }

  /**
   * Destroy the renderer
   */
  destroy(): void {
    this.backgroundRect = null;
    this.titleText = null;
    super.destroy();
  }
}
